<template>
  <el-scrollbar
    ref="scrollbar"
    :view-style="viewStyle"
    class="progress-stepper"
    :class="{ 'progress-stepper--live': !isCompleted }"
  >
    <el-steps
      :active="currentIndex + 1"
      align-center
    >
      <el-step
        v-for="item in items"
        :ref="item.key"
        :key="item.key"
        :icon="item.icon"
        :status="item.status"
        :class="{ 'progress-stepper__step--selectable': item.isSelectable }"
        @click.native="select(item)"
      >
        <el-tooltip
          slot="title"
          :content="item.label"
          :disabled="!item.isTruncated"
          placement="top"
        >
          <!-- No handler of its own: its click, keyboard included, bubbles to the step. -->
          <el-button
            :type="item.isSelected ? 'primary' : 'text'"
            :disabled="!item.isSelectable"
            :aria-pressed="String(item.isSelected)"
            size="mini"
          >
            {{ item.shortLabel }}
          </el-button>
        </el-tooltip>
        <template slot="description">
          <div v-if="item.date">
            {{ item.date }}
          </div>
          <div v-if="item.isCurrent">
            {{ $t('sampleProgress.steps.current') }}
          </div>
        </template>
      </el-step>
    </el-steps>
  </el-scrollbar>
</template>

<script>
import moment from 'moment';
import { addResizeListener, removeResizeListener } from 'element-ui/lib/utils/resize-event';
import { PROGRESS_STEP_KINDS } from '@/config/constants';
import progressStep from '@/services/progressStep';

// Narrowest a step can get before its title and date stop fitting: below it the stepper scrolls
// instead of squashing the steps.
const STEP_MIN_WIDTH_REM = 10;
const LABEL_MAX_LENGTH = 16;
const PLACEHOLDER_KEY = 'completed-placeholder';

const ICONS = {
  [PROGRESS_STEP_KINDS.SENT]: 'fas fa-paper-plane',
  [PROGRESS_STEP_KINDS.EXTERNAL]: 'fas fa-vial',
  [PROGRESS_STEP_KINDS.COMPLETED]: 'fas fa-check',
};

export default {
  name: 'ProgressStepper',
  props: {
    // Rows of the samplestepprogress view, ordered by stepPosition.
    steps: {
      type: Array,
      required: true,
    },
    selectedStepId: {
      type: Number,
      default: null,
    },
  },
  computed: {
    isCompleted() {
      return this.steps.some(step => step.kind === PROGRESS_STEP_KINDS.COMPLETED);
    },
    // Every sample eventually ends, so a sample not completed yet shows where it is heading.
    items() {
      const currentStepIndex = this.steps.findIndex(step => step.isCurrent);
      const items = this.steps.map((step, index) => this.toItem(step, index, currentStepIndex));
      return this.isCompleted ? items : [...items, this.completedPlaceholder()];
    },
    currentIndex() {
      return this.items.findIndex(item => item.isCurrent);
    },
    viewStyle() {
      return { minWidth: `${this.items.length * STEP_MIN_WIDTH_REM}rem` };
    },
  },
  mounted() {
    // el-scrollbar only watches its content, which stops resizing once the container gets
    // narrower than the steps' minimum width: without this the scrollbar would not appear.
    addResizeListener(this.$el, this.$refs.scrollbar.update);
    this.scrollToCurrentStep();
  },
  beforeDestroy() {
    removeResizeListener(this.$el, this.$refs.scrollbar.update);
  },
  methods: {
    // A step after the current one is reached but has not begun yet: a stability reaches every
    // time point at acceptance. A scheduled step shows the day it begins, not the day it was
    // reached.
    toItem(step, index, currentStepIndex) {
      const label = progressStep.label(step);
      const isTruncated = label.length > LABEL_MAX_LENGTH;

      return {
        key: String(step.stepId),
        stepId: step.stepId,
        label,
        shortLabel: isTruncated ? `${label.slice(0, LABEL_MAX_LENGTH - 1)}…` : label,
        isTruncated,
        icon: ICONS[step.kind],
        date: moment(step.startsAt || step.firstOccurredAt).format('DD/MM/YYYY'),
        isCurrent: step.isCurrent,
        isSelected: step.stepId === this.selectedStepId,
        isSelectable: true,
        status: this.statusOf(index, currentStepIndex),
      };
    },
    statusOf(index, currentStepIndex) {
      if (index === currentStepIndex) return 'process';
      return index < currentStepIndex ? 'finish' : 'wait';
    },
    // el-step has no click event: the whole step listens, so the icon and the line select too.
    select(item) {
      if (item.isSelectable) this.$emit('select', item.stepId);
    },
    completedPlaceholder() {
      const label = progressStep.label({ kind: PROGRESS_STEP_KINDS.COMPLETED });

      return {
        key: PLACEHOLDER_KEY,
        stepId: null,
        label,
        shortLabel: label,
        isTruncated: false,
        icon: ICONS[PROGRESS_STEP_KINDS.COMPLETED],
        date: null,
        isCurrent: false,
        isSelected: false,
        isSelectable: false,
        status: 'wait',
      };
    },
    // The current step is the furthest one, off screen on a long stepper.
    scrollToCurrentStep() {
      const current = this.items[this.currentIndex];
      if (!current) return;

      const [step] = this.$refs[current.key];
      const { wrap } = this.$refs.scrollbar;
      wrap.scrollLeft = step.$el.offsetLeft - (wrap.clientWidth - step.$el.offsetWidth) / 2;
    },
  },
};
</script>

<style lang="scss">
// el-step is not interactive by design: this is the one cue it needs to read as clickable.
.progress-stepper__step--selectable {
  cursor: pointer;
}

// One breath: the halo of the current step and the light on its way to the next share it.
$progress-stepper-breath: 2.4s;
// The halo grows out of the icon glyph. Fully grown it reaches past the icon box by less than
// the gap between the box and the title button below it, so it never runs into the button.
$progress-stepper-icon-height: 1.5rem;
$progress-stepper-halo-size: 0.875rem;
$progress-stepper-halo-scale: 2.4;
// How far the grown halo reaches above the icon, which is the top of the scroll area.
$progress-stepper-halo-grown: $progress-stepper-halo-size * $progress-stepper-halo-scale;
$progress-stepper-halo-room: ($progress-stepper-halo-grown - $progress-stepper-icon-height) * 0.5;

// The path fills calmly instead of snapping into place.
.progress-stepper .el-step__line-inner {
  transition-duration: 0.6s;
}

// Until the sample is completed the page shows work going on: the current step breathes and a
// light runs on toward the next step. A completed sample stays still.
.progress-stepper--live {
  // el-scrollbar clips what overflows its scroll area: the halo gets room inside it, taken back
  // from the card's padding so the stepper does not move.
  margin-top: -$progress-stepper-halo-room;

  .el-scrollbar__view {
    padding-top: $progress-stepper-halo-room;
  }

  .el-step__head.is-process {
    // Centred on the icon box, which is wider than tall.
    .el-step__icon::after {
      content: '';
      position: absolute;
      z-index: -1;
      top: 50%;
      left: 50%;
      width: $progress-stepper-halo-size;
      height: $progress-stepper-halo-size;
      margin-top: -$progress-stepper-halo-size * 0.5;
      margin-left: -$progress-stepper-halo-size * 0.5;
      border-radius: 50%;
      background-color: $--color-primary;
      opacity: 0;
      will-change: transform, opacity;
      animation: progress-stepper-halo $progress-stepper-breath ease-out infinite;
    }

    .el-step__line {
      overflow: hidden;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 40%;
        background-image: linear-gradient(90deg, transparent, $--color-primary, transparent);
        will-change: transform;
        animation: progress-stepper-flow $progress-stepper-breath ease-in-out infinite;
      }
    }
  }
}

// Starts and ends invisible, so the loop never shows where it restarts.
@keyframes progress-stepper-halo {
  0% {
    opacity: 0;
    transform: scale(1);
  }

  20% {
    opacity: 0.3;
  }

  100% {
    opacity: 0;
    transform: scale($progress-stepper-halo-scale);
  }
}

// From fully before the line to fully past it, the same: the restart is out of sight.
@keyframes progress-stepper-flow {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(250%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .progress-stepper .el-step__line-inner {
    transition-duration: 0s;
  }

  .progress-stepper--live .el-step__head.is-process {
    .el-step__icon::after,
    .el-step__line::after {
      animation: none;
    }
  }
}
</style>
